import { loadConnectAndInitialize } from '@stripe/connect-js';
import config from './config';

// Stripe Connect configuration
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
  'pk_live_51RReDnGUE37RioGbDropDxT9lh1rs95faQxdEAMj6Fclqhi5pFjrgfwNtgpI70k7iKQpjVvQj8UoTy24JyRwE31W00Euezlf74';

/**
 * Fetch client secret from the server for account sessions
 * @param {string} accountId - The connected account ID
 * @param {string} component - The component type ('account_onboarding' or 'account_management')
 * @returns {Promise<string>} The client secret for the account session
 */
export const fetchClientSecret = async (accountId, component = 'account_onboarding') => {
  console.log('fetchClientSecret called with accountId:', accountId);
  
  // Handle case where accountId is not provided (during initial app load)
  if (!accountId) {
    console.warn('No accountId provided to fetchClientSecret - returning placeholder');
    // Return a promise that resolves to null instead of throwing
    return Promise.resolve(null);
  }

  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('🔑 Fetching client secret for account:', accountId, 'component:', component);

    // Determine the endpoint based on component type
    const endpoint = component === 'account_management' 
      ? `${config.API_URL}/stripe/account_update_session`
      : `${config.API_URL}/stripe/account_session`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        account_id: accountId,
        return_url: `${window.location.origin}/dashboard/settings?connect=complete`,
        refresh_url: `${window.location.origin}/dashboard/settings?connect=refresh`
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to create account session');
    }

    // Extract client secret from the response
    // For Stripe Connect, the client secret is typically in the account link URL
    const accountLinkUrl = data.onboarding?.url || data.update?.url;
    
    if (!accountLinkUrl) {
      throw new Error('No account link URL received from server');
    }

    // For Connect.js, we need to extract or use the full URL as the client secret
    // The URL from account links IS the client secret for embedded components
    console.log('✅ Client secret (account link) fetched successfully');
    return accountLinkUrl;

  } catch (error) {
    console.error('❌ Error fetching client secret:', error);
    
    // For authentication errors, don't try fallback
    if (error.message.includes('authentication') || error.message.includes('token')) {
      // Return null instead of throwing to prevent app crashes
      console.warn('Authentication error in fetchClientSecret, returning null');
      return null;
    }

    // Fallback behavior - attempt to create a basic account session
    try {
      console.log('🔄 Attempting fallback client secret fetch...');
      
      // Fallback: try to create a basic account session without custom URLs
      const fallbackResponse = await fetch(`${config.API_URL}/stripe/account_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          account_id: accountId
        })
      });

      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        const fallbackUrl = fallbackData.onboarding?.url;
        
        if (fallbackUrl) {
          console.log('✅ Fallback client secret fetched successfully');
          return fallbackUrl;
        }
      }
    } catch (fallbackError) {
      console.error('❌ Fallback also failed:', fallbackError);
    }

    // Return null instead of throwing to prevent app crashes
    console.warn('All fetchClientSecret attempts failed, returning null');
    return null;
  }
};

/**
 * Initialize Stripe Connect instance
 * @returns {Promise<StripeConnectInstance>} The initialized Stripe Connect instance
 */
export const initializeStripeConnect = async () => {
  try {
    console.log('🔄 Initializing Stripe Connect...');

    // Validate publishable key
    if (!STRIPE_PUBLISHABLE_KEY) {
      throw new Error('Stripe publishable key is not configured');
    }

    if (!STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
      throw new Error('Invalid Stripe publishable key format');
    }

    // Initialize Connect with configuration
    const stripeConnectInstance = loadConnectAndInitialize({
      publishableKey: STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret: fetchClientSecret,
      appearance: {
        overlays: 'dialog',
        variables: {
          colorPrimary: '#1976d2', // Your brand primary color
          colorBackground: '#ffffff',
          colorText: '#262626',
          colorDanger: '#df1b41',
          fontFamily: 'Roboto, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px'
        }
      },
      // Optional: Configure locale
      locale: 'en'
    });

    console.log('✅ Stripe Connect initialized successfully');
    return stripeConnectInstance;

  } catch (error) {
    console.error('❌ Failed to initialize Stripe Connect:', error);
    throw error;
  }
};

/**
 * Create account session for onboarding
 * @param {string} accountId - The connected account ID
 * @returns {Promise<Object>} Account session details
 */
export const createAccountSession = async (accountId) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    console.log('🔗 Creating account session for:', accountId);

    const response = await fetch(`${config.API_URL}/stripe/account_session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        account_id: accountId,
        return_url: `${window.location.origin}/dashboard/settings?connect=complete`,
        refresh_url: `${window.location.origin}/dashboard/settings?connect=refresh`
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create account session');
    }

    const data = await response.json();
    console.log('✅ Account session created successfully');
    return data;

  } catch (error) {
    console.error('❌ Error creating account session:', error);
    throw error;
  }
};

/**
 * Create dashboard login link for connected account
 * @param {string} accountId - The connected account ID
 * @returns {Promise<Object>} Dashboard login details
 */
export const createDashboardLink = async (accountId) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    console.log('🎛️ Creating dashboard link for:', accountId);

    const response = await fetch(`${config.API_URL}/stripe/dashboard_link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        account_id: accountId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create dashboard link');
    }

    const data = await response.json();
    console.log('✅ Dashboard link created successfully');
    return data;

  } catch (error) {
    console.error('❌ Error creating dashboard link:', error);
    throw error;
  }
};

/**
 * Get account balance for connected account
 * @param {string} accountId - The connected account ID
 * @returns {Promise<Object>} Account balance details
 */
export const getAccountBalance = async (accountId) => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }

    console.log('💰 Fetching balance for account:', accountId);

    const response = await fetch(`${config.API_URL}/stripe/balance/${accountId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch account balance');
    }

    const data = await response.json();
    console.log('✅ Account balance fetched successfully');
    return data;

  } catch (error) {
    console.error('❌ Error fetching account balance:', error);
    throw error;
  }
};

// Export the configuration for use in components
export const stripeConnectConfig = {
  publishableKey: STRIPE_PUBLISHABLE_KEY,
  fetchClientSecret,
  createAccountSession,
  createDashboardLink,
  getAccountBalance
};

export default stripeConnectConfig;